import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  FormProvider,
  getDuration,
  getValidationResolver,
  OptionKey,
  SubmitHandler,
  useForm,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { VALIDATORS } from '@/common';
import { ERROR_ELEMENT_SCROLL_CLASS_NAME } from '@/common/constants';
import Permissions from '@/common/permissions';
import { Accordion } from '@/components';
import { Access, Topbar } from '@/modules/core/components';
import ModuleWrapper from '@/modules/plan/ModuleWrapper';
import globalTheme from '@/styles/theme';

import { ALWAYS_ON_IDS } from '../queries/usePermissionsList.query';
import { ADD_CONTACT_ID, EDIT_CONTACT_ID, IRoleSetup, NO_ACCESS_ROLE_ID } from '../role.types';

import RoleSetupConfirmModal from './RoleSetupConfirmModal/RoleSetupConfirmModal';
import BasicProperties from './RoleSetupForm/BasicProperties';
import FieldLevelAccessSection from './RoleSetupForm/FieldLevelAccessSection';
import PermissionToggleSection from './PermissionToggleSection';
import RoleSetupFinal from './RoleSetupFinal';
import { AffectedUsersCountSection, PermissionsCopyModal } from './RoleSetupForm';
import RoleSetupSidebar from './RoleSetupSidebar';
import { useRoleSetupStore } from './stores';
import RoleSetupActivityLog from './RoleSetupActivityLog';

interface Props {
  isEditMode?: boolean;
  isLoading?: boolean;
}
const PERMISSIONS_ERROR_MESSAGE = 'At least one permission should be selected';
const RoleSetup: React.FC<Props> = ({ isEditMode, isLoading = false }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const state = useRoleSetupStore((state) => state.state);
  const isDisabledPermissions = useMemo(() => state.id === NO_ACCESS_ROLE_ID, [state.id]);

  const errorFields = useRoleSetupStore((state) => state.errorFields);

  const isRoleExists = useMemo(
    () => Boolean(state.id),
    [state.id]
  );
  const methods = useForm<IRoleSetup>({
    defaultValues: state,

    resolver: getValidationResolver({
      name: VALIDATORS.REQUIRED_STRING.test({
        test: (value) => {
          const fieldValue = errorFields?.name?.value;
          if (!fieldValue) {
            return true;
          }
          return value !== fieldValue;
        },
        message: errorFields?.name?.message,
      }),
      allowedForContactsByRoleId: VALIDATORS.ARRAY.when('permissions', {
        is: (values: OptionKey[]) => {
          return !!values.includes(ADD_CONTACT_ID) || !!values.includes(EDIT_CONTACT_ID);
        },
        then: () => VALIDATORS.REQUIRED_ARRAY,
        otherwise: () => VALIDATORS.ARRAY,
      }),
      fieldLevelAccessId: VALIDATORS.REQUIRED_STRING,
      permissions: isDisabledPermissions
        ? VALIDATORS.ARRAY
        : VALIDATORS.ARRAY_REQUIRED.min(1, PERMISSIONS_ERROR_MESSAGE).test({
            test: (value) => {
              const formattedList = (value as string[]).map((item) => _.toNumber(item));

              return _.difference(formattedList, ALWAYS_ON_IDS).length > 0;
            },
            message: PERMISSIONS_ERROR_MESSAGE,
          }),
    }),
  });

  useEffect(() => {
    if (errorFields?.name?.value) {
      methods.trigger('name');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorFields?.name?.value]);

  const handleApplyPermissions = useCallback(
    (ids: OptionKey[]) => {
      methods.reset({
        ...state,
        permissions: ids,
      });
    },
    [methods, state]
  );
  useEffect(() => {
    if (!isLoading && state.id && isEditMode) {
      methods.reset(state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, state.id]);

  const setState = useRoleSetupStore((state) => state.setState);
  const handleSubmit: SubmitHandler<IRoleSetup> = useCallback(
    (data) => {
      setState(data);
      setShowConfirm(true);
    },
    [setState]
  );
  const handleValidate = useCallback(() => {
    setTimeout(
      () => {
        const elem = document.getElementsByClassName(ERROR_ELEMENT_SCROLL_CLASS_NAME)?.[0];

        if (elem) {
          elem.scrollIntoView({ behavior: 'smooth' });
        }
      },
      getDuration({
        seconds: 0.5,
      })
    );
  }, []);

  return (
    <>
      <RoleSetupConfirmModal
        isEditMode={isEditMode}
        visible={showConfirm}
        isDirty={methods.formState.isDirty}
        onSetVisible={setShowConfirm}
      />
      <FormProvider {...methods} key={state.id}>
        <Box fill direction="row" background="module">
          <RoleSetupSidebar isLoading={isLoading} />

          <Box
            direction="column"
            id="role_setup"
            data-testid="form-preloader"
            style={{ position: 'relative' }}
            fill
            overflow="auto"
            background="canvas"
          >
            <Box direction="column">
              <div>
                <Topbar isSticky>
                  <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <AppButton width="100px" type="submit" onClick={handleValidate}>
                      Save
                    </AppButton>
                  </form>
                </Topbar>

                <Box pad={{ horizontal: '40px', vertical: 's' }} width={globalTheme.defaultContentWidth}>
                  <Box direction="row" align="center" justify="between">
                    <Accordion />
                    <Access
                      accessRequirements={[
                        {
                          permission: Permissions.EDIT_ROLES_AND_SECURITY,
                        },
                      ]}
                    >
                      {state.id !== NO_ACCESS_ROLE_ID && (
                        <PermissionsCopyModal onApply={handleApplyPermissions} />
                      )}
                    </Access>
                  </Box>
                  <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <ModuleWrapper
                      testId="basic-properties"
                      anchorId="basic_properties"
                      title="Basic Properties"
                      expandless
                    >
                      <BasicProperties
                        isLoading={isLoading}
                        defaultValues={state}
                        isDisableLevel={isEditMode}
                        isEditMode={true}
                      />
                      <AffectedUsersCountSection
                        organizationId={state?.organizationId}
                        isLoading={isLoading}
                        roleId={state.id}
                      />
                      {state.id !== NO_ACCESS_ROLE_ID && (
                        <FieldLevelAccessSection isEditMode isLoading={isLoading} />
                      )}
                    </ModuleWrapper>

                    <PermissionToggleSection
                      isDisabled={isDisabledPermissions}
                      roleId={state.id}
                      isLoading={isLoading}
                    />
                    
                    {isRoleExists ? <RoleSetupActivityLog /> : null}

                    <ModuleWrapper testId="final_setup" anchorId="final_setup" title="Final Setup">
                      <RoleSetupFinal onClick={handleValidate} />
                    </ModuleWrapper>
                  </form>
                </Box>
              </div>
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </>
  );
};
export default React.memo(RoleSetup);
